/* @flow */

import React, { Component } from 'react'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import IfAdmin from '../IfAdmin'
import Condition from '../Condition'
import CustomerSelect from '../CustomerSelect/'
import PollFormToggles from './PollFormToggles'

import type { Poll, Tag } from '../../types'
import {
  newMinLengthValidator,
  newMaxLengthValidator,
  pollVerticalValidator,
} from '../../helpers'
import {
  MIN_POLL_LABEL_LENGTH,
  MAX_POLL_LABEL_LENGTH,
  MAX_SLIDER_VALUE_LENGTH,
  MAX_SLIDER_STEPS_LENGTH,
} from '../CheckedoutPoll'

import Controls from './Controls'
import TagsSelect from './TagsSelect'
import OpinaryInput from '../Controls/OpinaryInput'
import OpinarySelect from '../Controls/OpinarySelect'
import HoverTooltip from '../Controls/HoverTooltip'

import { getTags } from '../../stores/tags'
import { getSlider } from '../../stores/pollsStore'
import {
  selectCheckedoutPoll,
  updateCheckedoutPoll,
} from '../../stores/pollBuilderCheckedoutPoll'

import { Handler } from './handler'
import {
  selectCustomerById,
  selectCustomerVerticals,
} from '../../stores/customersStore'

type Props = {
  poll: Poll,
  isAdmin: boolean,
  tags: Array<Tag>,
  sliderValues: Object,
  updatePoll: Object => void,
  customerVerticals: Array<Object>,
  customerConfig: Object,
}

type State = {
  sliderSteps: any,
}

const selectValues = [
  { value: '', label: 'None' },
  { value: '€', label: '€' },
  { value: '$', label: '$' },
  { value: '£', label: '£' },
  { value: 'Fr', label: 'Fr' },
  { value: 'CHF', label: 'CHF' },
  { value: '%', label: '%' },
  { value: '℃', label: '℃' },
  { value: '★', label: '★' },
  { value: '♥', label: '♥' },
  { value: 'km', label: 'km' },
  { value: 'km/h', label: 'km/h' },
  { value: 'mp/h', label: 'mp/h' },
  { value: 'Tore', label: 'Tore' },
]

let stepSizeResult = 0

const language = navigator.language || navigator.languages[0]
const euroSymbolLeft = [
  'en',
  'en-GB',
  'en-US',
  'en-IE',
  'mt',
  'mt-MT',
  'nl',
  'nl-NL',
]

// Styles
const handlerPositioning = { transform: 'translate3d(0%, -68%, 0)' }
const ticksStyle = { borderRight: '1px solid black', position: 'relative' }
const tickNoBorderStyle = { border: 'none' }
const fieldNameColor = { color: '#9e9c9c' }
const tooltipIconStyle = { marginLeft: '5px' }
const unitLeftStyle = { marginLeft: '-15px', bottom: '13px' }
const unitRightStyle = { margin: 'auto 3px 13px' }
const outerSliderStyle = { height: '40px' }
const innerSliderStyle = { padding: '1% 0', justifyContent: 'space-between' }

const rangeSliderStyle = {
  height: '25px',
  width: '700px',
  backgroundColor: '#e1e1e1',
  borderLeft: '1px solid black',
  borderRight: '1px solid black',
  margin: 'auto',
}

const stepSizeStyle = {
  color: '#9e9c9c',
  zIndex: '2',
  transform: 'translate3d(-49%, -138%, 0px)',
  borderRadius: '4px',
  border: '1px solid #ccc',
  padding: '7px',
}

class SliderForm extends Component<Props, State> {
  state = {
    sliderSteps: '10',
  }

  onKeyPress = event => {
    const keyCode = event.keyCode || event.which
    const keyValue = String.fromCharCode(keyCode)
    if (/\+|-/.test(keyValue)) {
      event.preventDefault()
    }
  }

  render() {
    const {
      poll,
      isAdmin,
      tags,
      sliderValues,
      updatePoll,
      customerVerticals,
      customerConfig,
    } = this.props

    const minError = newMinLengthValidator(MIN_POLL_LABEL_LENGTH)
    const maxError = newMaxLengthValidator(MAX_SLIDER_VALUE_LENGTH)
    const maxErrorLong = newMaxLengthValidator(MAX_POLL_LABEL_LENGTH)
    const maxStepsError = newMaxLengthValidator(MAX_SLIDER_STEPS_LENGTH)
    const setPollAttribute = (key, value) => updatePoll({ [key]: value })
    const setCustomerId = customer => setPollAttribute('customer', customer)
    const setSelectedValue = value => setPollAttribute('sliderUnit', value)
    const verticalsError = pollVerticalValidator(
      poll,
      customerVerticals,
      customerConfig.enableVerticalTargeting
    )

    const isFormValid = () => {
      return (
        verticalsError == null &&
        minError(poll.header) == null &&
        maxErrorLong(poll.header) == null &&
        (minError(poll.sliderMin) == null &&
          maxError(poll.sliderMin) == null) &&
        (minError(poll.sliderMax) == null &&
          maxError(poll.sliderMax) == null) &&
        (minError(poll.sliderMax) == null && maxError(poll.sliderMax) == null)
      )
    }

    const updateName = ({ target }) => {
      const { value } = target

      value
        ? setPollAttribute('userEnteredId', true)
        : setPollAttribute('userEnteredId', false)

      setPollAttribute('name', value)
    }

    const updateStepSize = () => {
      let { sliderMax, sliderMin, sliderSteps } = sliderValues

      let stepSize =
        sliderSteps === 1
          ? sliderMin - sliderMax
          : (sliderMax - sliderMin) / (parseInt(sliderSteps, 10) - 1)

      stepSizeResult = Number.isInteger(stepSize)
        ? parseInt(sliderMin, 10) + stepSize
        : Number(parseInt(sliderMin, 10) + stepSize).toFixed(2)
    }

    const currencySymbolPlacement = (() => {
      if (
        poll.sliderUnit === '$' ||
        poll.sliderUnit === '£' ||
        (poll.sliderUnit === '€' && euroSymbolLeft.includes(language))
      ) {
        return true
      } else {
        return false
      }
    })()

    const handlerBlur = event => {
      let name = event.target.name

      event.target.name && event.target.value
        ? updatePoll({ [name]: false })
        : updatePoll({ [name]: true })
    }

    const makeChangeHandler = (key: string) => {
      updateStepSize()

      return (event: Event) => {
        if (event.target.value == null) {
          throw new Error('expected event.target.value to exist')
        }

        setPollAttribute(key, event.target.value)
      }
    }

    return (
      <form>
        <div className="w-100 relative mb4 flex">
          <span className="bg-op-con-dark op-con-light f6 ph4 h3 flex items-center">
            <span className="nowrap">Slider</span>
          </span>
          <span className="w-60 mh4 mr-auto">
            <IfAdmin>
              <CustomerSelect
                className="w-100 bb b--moon-gray"
                colorClass="gray"
                onChange={setCustomerId}
                customerId={poll.customer}
              />
            </IfAdmin>
          </span>
          <span className="f6 bg-near-white gray ph4 h3 flex items-center">
            <span>Example</span>
          </span>
        </div>

        <div className="w-100 flex flex-column items-center">
          <div className="w-90">
            {/* Question */}
            <OpinaryInput
              onChange={makeChangeHandler('header')}
              value={poll.header}
              className="w-100 mb2"
              inputClass="tc"
              colorClass="dark-gray"
              borderClass={
                maxErrorLong(poll.header) ? 'b--dark-red' : 'b--moon-gray'
              }
              maxLength={MAX_POLL_LABEL_LENGTH + 1}
              onBlur={handlerBlur}
              name="questionInput"
            />

            <Condition shouldRender={poll.questionInput ? true : false}>
              <div className="tl dark-red"> Field cannot be empty </div>
            </Condition>
            <Condition shouldRender={maxErrorLong(poll.header) != null}>
              <div className="tl dark-red">{maxErrorLong(poll.header)}</div>
            </Condition>

            <div className="m-auto tc mb-50" style={fieldNameColor}>
              <HoverTooltip
                className="di pointer hover-op-con-dark"
                colorClass="white"
                arrowClass="dark-gray"
                backgroundClass="bg-dark-gray"
                tip="Example: 'How much do you love Opinary?'"
              >
                Question
                <i
                  className="fa far fa-question-circle pointer hover-op-brand"
                  style={tooltipIconStyle}
                ></i>
              </HoverTooltip>
            </div>

            {/* Slider diagram */}
            <div className="w-100 tc" style={outerSliderStyle}>
              {/* Slider */}
              <div
                className="full-width full-height flexbox"
                style={rangeSliderStyle}
              >
                <div className="w-100 h-100 flex" style={innerSliderStyle}>
                  <span style={tickNoBorderStyle}></span>
                  {Array.from(
                    { length: parseInt(poll.sliderSteps, 10) - 1 },
                    (item, index) => (
                      <span
                        key={index}
                        style={
                          index === parseInt(poll.sliderSteps, 10) - 2
                            ? tickNoBorderStyle
                            : ticksStyle
                        }
                      >
                        {index === 0 && (
                          <span
                            className="tooltip absolute"
                            style={stepSizeStyle}
                          >
                            <HoverTooltip
                              className="pointer hover-op-con-dark inline-block"
                              colorClass="white"
                              arrowClass="dark-gray"
                              backgroundClass="bg-dark-gray"
                              tip="This value will be on your second step"
                            >
                              <span className="font-size-m">{`${stepSizeResult}`}</span>
                              <i
                                className="fa far fa-question-circle"
                                style={tooltipIconStyle}
                              />
                            </HoverTooltip>
                          </span>
                        )}
                      </span>
                    )
                  )}
                </div>
              </div>

              {/* Handler */}
              <div className="sliderHandler m-auto" style={handlerPositioning}>
                <Handler />
              </div>
            </div>

            <div className="flex m-auto w-700px">
              {/* Minimum value field */}
              <div className="w-30">
                <div className="w-100 mb5">
                  <div className="flex relative">
                    {currencySymbolPlacement === true && (
                      <span
                        className="absolute font-size-m"
                        style={unitLeftStyle}
                      >
                        {poll.sliderUnit}
                      </span>
                    )}
                    <OpinaryInput
                      id="sliderMin"
                      onKeyPress={event => this.onKeyPress(event)}
                      placeholder="0"
                      onChange={makeChangeHandler('sliderMin')}
                      value={poll.sliderMin}
                      className="w-100 mb2 f3"
                      colorClass="light-gray"
                      type="number"
                      maxLength={MAX_SLIDER_VALUE_LENGTH + 1}
                      onBlur={handlerBlur}
                      name="sliderMinInput"
                    />
                    {currencySymbolPlacement === false && (
                      <span className="font-size-m" style={unitRightStyle}>
                        {poll.sliderUnit}
                      </span>
                    )}
                  </div>
                  <label style={fieldNameColor}>
                    <HoverTooltip
                      className="di pointer hover-op-con-dark"
                      colorClass="white"
                      arrowClass="dark-gray"
                      backgroundClass="bg-dark-gray"
                      tip="Enter your smallest value here"
                    >
                      Minimum value{' '}
                      <i
                        className="fa far fa-question-circle"
                        style={tooltipIconStyle}
                      />
                    </HoverTooltip>
                  </label>

                  <Condition shouldRender={poll.sliderMinInput ? true : false}>
                    <div className="tl dark-red"> Field cannot be empty </div>
                  </Condition>
                  <Condition shouldRender={maxError(poll.sliderMin) != null}>
                    <div className="dark-red">{maxError(poll.sliderMin)}</div>
                  </Condition>
                </div>
              </div>

              {/* Maximum value field */}
              <div className="w-30 ml-auto">
                <div className="w-100 mb5 tr">
                  <div className="flex relative">
                    {currencySymbolPlacement === true && (
                      <span
                        className="absolute font-size-m"
                        style={unitLeftStyle}
                      >
                        {poll.sliderUnit}
                      </span>
                    )}
                    <OpinaryInput
                      id="sliderMax"
                      onKeyPress={event => this.onKeyPress(event)}
                      placeholder="10"
                      value={poll.sliderMax}
                      onChange={makeChangeHandler('sliderMax')}
                      className="w-100 mb2 f3"
                      inputClass="tr"
                      colorClass="light-gray"
                      type="number"
                      maxLength={MAX_SLIDER_VALUE_LENGTH + 1}
                      onBlur={handlerBlur}
                      name="sliderMaxInput"
                    />
                    {currencySymbolPlacement === false && (
                      <span style={unitRightStyle}>{poll.sliderUnit}</span>
                    )}
                  </div>
                  <label style={fieldNameColor}>
                    <HoverTooltip
                      className="di pointer hover-op-con-dark"
                      colorClass="white"
                      arrowClass="dark-gray"
                      backgroundClass="bg-dark-gray"
                      tip="Enter your largest value here"
                    >
                      Maximum value{' '}
                      <i
                        className="fa far fa-question-circle"
                        style={tooltipIconStyle}
                      ></i>
                    </HoverTooltip>
                  </label>

                  <Condition shouldRender={poll.sliderMaxInput ? true : false}>
                    <div className="dark-red"> Field cannot be empty </div>
                  </Condition>
                  <Condition shouldRender={maxError(poll.sliderMax) != null}>
                    <div className="dark-red">{maxError(poll.sliderMax)}</div>
                  </Condition>
                </div>
              </div>
            </div>

            <div className="flex m-auto w-700px">
              {/* Slider units field */}
              <div className="w-30">
                <div className="w-100 mb4">
                  <OpinarySelect
                    className="w-100 h2 mb2 f3 bb b--moon-gray"
                    colorClass="b--moon-gray"
                    onChange={setSelectedValue}
                    options={selectValues}
                    creatable
                    value={poll.sliderUnit || ''}
                  />
                  <label style={fieldNameColor}>
                    <HoverTooltip
                      className="di pointer hover-op-con-dark"
                      colorClass="white"
                      arrowClass="dark-gray"
                      backgroundClass="bg-dark-gray"
                      tip="Select the value type for the slider, or leave it blank"
                    >
                      Slider units{' '}
                      <i
                        className="fa far fa-question-circle"
                        style={tooltipIconStyle}
                      ></i>
                    </HoverTooltip>
                  </label>
                </div>
              </div>

              {/* Number of steps field */}
              <div className="w-30 ml-auto">
                <div className="w-100 mb4 tr">
                  <OpinaryInput
                    id="sliderSteps"
                    onKeyPress={event => this.onKeyPress(event)}
                    placeholder="10"
                    value={poll.sliderSteps}
                    onChange={makeChangeHandler('sliderSteps')}
                    className="w-100 mb2 f3"
                    inputClass="tr"
                    colorClass="dark-gray"
                    type="number"
                    maxLength={MAX_SLIDER_STEPS_LENGTH + 1}
                    onBlur={handlerBlur}
                  />
                  <label style={fieldNameColor}>
                    <HoverTooltip
                      className="di pointer hover-op-con-dark"
                      colorClass="white"
                      arrowClass="dark-gray"
                      backgroundClass="bg-dark-gray"
                      tip="This represents the number of 'points' on the slider"
                    >
                      Number of steps{' '}
                      <i
                        className="fa far fa-question-circle"
                        style={tooltipIconStyle}
                      ></i>
                    </HoverTooltip>
                  </label>

                  <Condition shouldRender={poll.sliderSteps ? false : true}>
                    <div className="dark-red"> Field cannot be empty </div>
                  </Condition>
                  <Condition
                    shouldRender={maxStepsError(poll.sliderSteps) != null}
                  >
                    <div className="dark-red">
                      {maxStepsError(poll.sliderSteps)}
                    </div>
                  </Condition>
                </div>
              </div>
            </div>

            <label className="f6 gray db">Poll ID</label>
            {poll.id ? (
              <span className="gray mt1">{poll.name}</span>
            ) : (
              <OpinaryInput
                id="name"
                onChange={updateName}
                value={poll.name}
                className="w-40 f6"
              />
            )}

            <TagsSelect
              options={tags}
              className="w-40 mt4"
              value={poll.tags}
              onChange={val => setPollAttribute('tags', val)}
            />
          </div>

          <div className="w-90 flex mb4">
            <PollFormToggles
              poll={poll}
              toggleAttribute={setPollAttribute}
              isAdmin={isAdmin}
            />
          </div>
        </div>

        <div className="w-100 h3 bg-near-white flex items-center justify-center">
          <div className="w-90">
            <Controls poll={poll} isFormValid={isFormValid()} />
          </div>
        </div>
      </form>
    )
  }
}

const mapStateToProps = state => {
  const poll = selectCheckedoutPoll(state)
  return {
    tags: getTags(state),
    sliderValues: getSlider(state),
    customerVerticals: selectCustomerVerticals(state),
    customerConfig: poll ? selectCustomerById(state, poll.customer) : {},
  }
}

const mapDispatchToProps = {
  updatePoll: updateCheckedoutPoll,
}

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(SliderForm)



// WEBPACK FOOTER //
// ./src/components/PollForm/SliderPollForm.js